import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore'; 
import { Loader2, Link, UserPlus, AlertTriangle, CheckCircle, Search, User, Users, X, ChevronDown, Check, Save } from 'lucide-react';

// Fonction utilitaire pour l'année scolaire (inchangée)
const getCurrentSchoolYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); 

    if (month >= 8) { 
        return `${year}-${year + 1}`; 
    }
    return `${year - 1}-${year}`; 
};
const CURRENT_SCHOOL_YEAR = getCurrentSchoolYear();

const ParentChildLinking = ({ db, schoolId, userRole, setMessage, setError }) => {
    const [parents, setParents] = useState([]);
    const [students, setStudents] = useState([]); 
    const [selectedParentId, setSelectedParentId] = useState('');
    const [linkedChildrenIds, setLinkedChildrenIds] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    // États de recherche pour Parents et Étudiants
    const [searchParentTerm, setSearchParentTerm] = useState(''); 
    const [searchStudentTerm, setSearchStudentTerm] = useState('');
    
    // État pour le menu déroulant du parent
    const [isParentDropdownOpen, setIsParentDropdownOpen] = useState(false);


    const canManage = userRole === 'superAdmin' || userRole === 'admin';
    
    // --- UTILS MEMOIZED ---
    const selectedParent = useMemo(() => {
        return parents.find(p => p.id === selectedParentId) || null;
    }, [parents, selectedParentId]);

    // Liste des enfants actuellement liés au parent sélectionné (mis à jour après la sauvegarde)
    const linkedChildrenMap = useMemo(() => {
        return linkedChildrenIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
    }, [linkedChildrenIds]);
    
    // --- DATA FETCHING (Parents & Students) ---
    const fetchParentsAndStudents = useCallback(async () => {
        if (!db || !schoolId || !canManage) {
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        setError(null);

        try {
            // 1. Fetch Parents (users with role 'parent')
            const parentsQuery = query(
                collection(db, 'users'), 
                where('role', '==', 'parent'),
                where('schoolId', '==', schoolId)
            );
            const parentsSnap = await getDocs(parentsQuery);
            const fetchedParents = parentsSnap.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                name: doc.data().name || `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim() || doc.id
            }));
            setParents(fetchedParents);

            // 2. Fetch Enrollments for Current Year to find ENROLLED students
            const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
            const qEnrollment = query(
                enrollmentCollectionRef,
                where('schoolYear', '==', CURRENT_SCHOOL_YEAR)
            );
            const enrollmentSnap = await getDocs(qEnrollment);

            const enrolledStudentsData = {};
            const eleveIdsToFetch = new Set();
            const classIdsToFetch = new Set();

            enrollmentSnap.docs.forEach(doc => {
                const data = doc.data();
                // Utiliser studentId comme eleveId
                eleveIdsToFetch.add(data.studentId);
                classIdsToFetch.add(data.classId);
                enrolledStudentsData[data.studentId] = { classId: data.classId };
            });

            // 3. Fetch Class Names
            const classNamesMap = {};
            await Promise.all(Array.from(classIdsToFetch).map(async (classId) => {
                if (!classId) return;
                const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                const classDocSnap = await getDoc(classDocRef);
                if (classDocSnap.exists()) {
                    classNamesMap[classId] = classDocSnap.data().name || classId;
                }
            }));

            // 4. Fetch Eleve Details (only for enrolled students)
            const eleveFetches = Array.from(eleveIdsToFetch).map(async (eleveId) => {
                const eleveDocRef = doc(db, 'schools', schoolId, 'eleves', eleveId);
                const eleveDocSnap = await getDoc(eleveDocRef);

                if (eleveDocSnap.exists()) {
                    const student = eleveDocSnap.data();
                    const childName = student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || eleveId;
                    const classData = enrolledStudentsData[eleveId];
                    
                    return { 
                        id: eleveId,
                        // Utiliser le nom de la propriété "name" pour la recherche et l'affichage
                        name: childName, 
                        className: classNamesMap[classData.classId] || 'Inconnue',
                        // ... tout autre champ pertinent
                    };
                }
                return null; 
            });

            const fetchedStudents = (await Promise.all(eleveFetches)).filter(s => s !== null);
            setStudents(fetchedStudents);

        } catch (err) {
            console.error("Erreur lors du chargement des données:", err);
            setError("Erreur de chargement des parents et des élèves inscrits.");
        } finally {
            setIsLoading(false);
        }
    }, [db, schoolId, canManage, setError]);
    
    useEffect(() => {
        fetchParentsAndStudents();
    }, [fetchParentsAndStudents]); 

    // --- PARENT SELECTION EFFECT ---
    useEffect(() => {
        if (selectedParent) {
            const initialLinked = selectedParent.childrenEleveIds || [];
            setLinkedChildrenIds(initialLinked);
        } else {
            setLinkedChildrenIds([]);
        }
    }, [selectedParent]);

    // --- FILTERING LOGIC (Inchangé) ---
    const filteredParents = useMemo(() => {
        if (!searchParentTerm) return parents;
        const search = searchParentTerm.toLowerCase();
        return parents.filter(parent => 
            parent.name.toLowerCase().includes(search) ||
            parent.email.toLowerCase().includes(search)
        );
    }, [parents, searchParentTerm]);

    const filteredStudents = useMemo(() => {
        if (!searchStudentTerm) return students;
        const search = searchStudentTerm.toLowerCase();
        return students.filter(student => 
            student.name.toLowerCase().includes(search) ||
            student.className.toLowerCase().includes(search)
        );
    }, [students, searchStudentTerm]);

    // --- HANDLERS ---
    
    // Gestion de la sélection d'un parent
    const handleParentSelect = (parentId) => {
        setSelectedParentId(parentId);
        setIsParentDropdownOpen(false); 
        setSearchParentTerm(''); 
    };

    // Gestion de la bascule de lien d'un enfant
    const handleToggleChildLink = useCallback((studentId) => {
        setLinkedChildrenIds(prevIds => {
            if (prevIds.includes(studentId)) {
                return prevIds.filter(id => id !== studentId);
            } else {
                return [...prevIds, studentId];
            }
        });
    }, []);

    // Gestion de la sauvegarde
    const handleSave = useCallback(async () => {
        if (!selectedParentId) {
            setError("Veuillez sélectionner un parent.");
            return;
        }

        setIsSaving(true);
        setError(null);
        setMessage(null);

        try {
            const parentDocRef = doc(db, 'users', selectedParentId);
            
            await updateDoc(parentDocRef, {
                childrenEleveIds: linkedChildrenIds,
            });

            // Mettre à jour la liste des parents en mémoire pour refléter le changement
            setParents(prevParents => prevParents.map(p => 
                p.id === selectedParentId ? { ...p, childrenEleveIds: linkedChildrenIds } : p
            ));

            setMessage("Liaisons Parent-Enfants sauvegardées avec succès !");

        } catch (error) {
            console.error("Erreur de sauvegarde:", error);
            setError("Échec de la sauvegarde des liaisons. Veuillez réessayer.");
        } finally {
            setIsSaving(false);
        }
    }, [db, selectedParentId, linkedChildrenIds, setError, setMessage]);
    
    // --- Composant de l'action de Sauvegarde ---
    const SaveActionBlock = () => (
        <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-xl shadow-inner mt-4 flex justify-between items-center sticky top-0 z-20">
            <div className="flex items-center space-x-4">
                <Save className="w-8 h-8 text-yellow-700" />
                <div>
                    <span className="text-gray-800 font-bold block">Action de Sauvegarde</span>
                    <span className="text-sm text-gray-600">
                        {linkedChildrenIds.length} enfant(s) sélectionné(s) pour la liaison.
                    </span>
                </div>
            </div>
            
            <button
                onClick={handleSave}
                disabled={!selectedParentId || isSaving}
                className="inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-xl shadow-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300 transform hover:scale-[1.01]"
            >
                {isSaving ? (
                    <>
                        <Loader2 className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" />
                        Sauvegarde en cours...
                    </>
                ) : (
                    <>
                        <Check className="-ml-1 mr-3 h-6 w-6" />
                        Sauvegarder les Liaisons
                    </>
                )}
            </button>
        </div>
    );

    // --- RENDERING ---

    if (!canManage) {
        return (
            <div className="p-10 text-center bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-lg">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">Accès Refusé</p>
                <p>Vous n'avez pas les autorisations nécessaires pour gérer les liaisons parent-élève.</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-2xl space-y-8 max-w-4xl mx-auto">
            
            {/* TITRE ET ÉTAT GLOBAL */}
            <div className="border-b pb-4 mb-4 flex items-center justify-between">
                <h2 className="text-3xl font-extrabold text-gray-900 flex items-center">
                    <Link className="w-8 h-8 mr-3 text-indigo-600" />
                    Gestion des Liaisons Parent-Élève
                </h2>
                {isLoading && (
                    <span className="text-sm font-medium text-indigo-600 flex items-center">
                        <Loader2 className="animate-spin w-4 h-4 mr-2" />
                        Chargement des données...
                    </span>
                )}
            </div>

            {/* SÉLECTION DU PARENT (CARD 1) */}
            <div className="p-6 bg-indigo-50 border border-indigo-200 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-indigo-800 mb-4 flex items-center">
                    <User className="w-6 h-6 mr-2" /> 
                    1. Sélectionner le Parent (Compte Utilisateur)
                </h3>
                
                {/* Custom Dropdown/Select */}
                <div className="relative">
                    <button
                        onClick={() => setIsParentDropdownOpen(!isParentDropdownOpen)}
                        className="w-full text-left bg-white border border-gray-300 rounded-lg p-3 flex items-center justify-between shadow-sm hover:border-indigo-500 transition duration-150"
                    >
                        {selectedParent ? (
                            <span className="font-semibold text-gray-800">
                                {selectedParent.name} <span className="text-sm text-gray-500 ml-2">({selectedParent.email})</span>
                            </span>
                        ) : (
                            <span className="text-gray-500">Choisir un parent...</span>
                        )}
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isParentDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                    </button>

                    {isParentDropdownOpen && (
                        <div className="absolute z-30 w-full mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-60 overflow-y-auto">
                            <div className="p-2 border-b">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom ou email..."
                                        value={searchParentTerm}
                                        onChange={(e) => setSearchParentTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        onClick={(e) => e.stopPropagation()} 
                                    />
                                </div>
                            </div>
                            
                            {filteredParents.length > 0 ? (
                                filteredParents.map(parent => (
                                    <div
                                        key={parent.id}
                                        onClick={() => handleParentSelect(parent.id)}
                                        className={`p-3 cursor-pointer hover:bg-indigo-100 flex items-center justify-between transition duration-150 ${selectedParentId === parent.id ? 'bg-indigo-200 font-bold' : ''}`}
                                    >
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{parent.name}</p>
                                            <p className="text-xs text-gray-500">{parent.email}</p>
                                        </div>
                                        {selectedParentId === parent.id && <Check className="w-4 h-4 text-indigo-600" />}
                                    </div>
                                ))
                            ) : (
                                <p className="p-3 text-center text-sm text-gray-500">Aucun parent trouvé.</p>
                            )}
                        </div>
                    )}
                </div>
                
                {selectedParent && (
                    <p className="mt-3 text-sm text-indigo-700 font-medium">
                        Parent sélectionné: **{selectedParent.name}**
                        <span className="block text-xs text-indigo-600">Enfants déjà liés ({selectedParent.childrenEleveIds?.length || 0}) : {selectedParent.childrenEleveIds?.join(', ') || 'Aucun'}</span>
                    </p>
                )}
            </div>
            
            {/* ACTION DE SAUVEGARDE (DÉPLACÉE EN HAUT) */}
            {selectedParentId && <SaveActionBlock />}

            {/* LIAISON DES ÉLÈVES (CARD 2) */}
            {selectedParentId && (
                <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <Users className="w-6 h-6 mr-2 text-indigo-600" /> 
                        2. Lier les Élèves Inscrit ({students.length} Élèves)
                    </h3>
                    <p className='text-sm text-gray-500 mb-4'>Seuls les élèves inscrits pour l'année scolaire en cours ({CURRENT_SCHOOL_YEAR}) sont affichés.</p>
                    
                    {/* Search Student Input */}
                    <div className="relative mb-5">
                        <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher un élève par nom ou classe..."
                            value={searchStudentTerm}
                            onChange={(e) => setSearchStudentTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-inner focus:ring-indigo-500 focus:border-indigo-500 text-base transition duration-150"
                        />
                    </div>
                    
                    <div className="max-h-[500px] overflow-y-auto pr-2">
                        {filteredStudents.length === 0 ? (
                            <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="text-lg text-gray-600">Aucun élève trouvé avec le filtre actuel.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {filteredStudents.map(student => {
                                    const isLinked = linkedChildrenMap[student.id];
                                    const cardClass = isLinked ? 'bg-green-50 border-green-300 shadow-md' : 'bg-gray-50 border-gray-300 hover:bg-gray-100';
                                    const icon = isLinked ? <CheckCircle className="w-6 h-6 text-green-600" /> : <Link className="w-6 h-6 text-indigo-600" />;
                                    
                                    return (
                                        <div
                                            key={student.id}
                                            onClick={() => handleToggleChildLink(student.id)}
                                            className={`p-4 rounded-lg border cursor-pointer flex items-center justify-between transition duration-200 ${cardClass}`}
                                            title={isLinked ? "Cliquer pour délister" : "Cliquer pour lier"}
                                        >
                                            <div className="flex items-center">
                                                {icon}
                                                <div className="ml-3">
                                                    <p className="text-base font-semibold text-gray-900">{student.name}</p>
                                                    <p className="text-sm text-gray-600">Classe: {student.className}</p>
                                                </div>
                                            </div>
                                            <div className={`px-3 py-1 text-xs font-bold rounded-full ${isLinked ? 'bg-green-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}>
                                                {isLinked ? 'LIÉ' : 'DÉLIÉ'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Action de Sauvegarde (répétée en bas pour les petites listes) */}
             {selectedParentId && <SaveActionBlock />}
        </div>
    );
};

export default ParentChildLinking;